import {
  Badge,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  makeStyles,
  Popover,
  SvgIcon,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { capitalCase } from 'change-case';
import React, { useRef, useState, useEffect } from 'react';
import { Settings as SettingsIcon, User as UserIcon } from 'react-feather';
import Logout from 'src/components/auth/Logout';
import { THEMES } from 'src/constants';
import useSettings from 'src/hooks/useSettings';
import { useKeycloak } from '@react-keycloak/web';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1
  },
  badge: {
    height: 10,
    width: 10,
    borderRadius: 5,
    marginTop: 10,
    marginRight: 5
  },
  popover: {
    width: 320,
    padding: theme.spacing(2)
  }
}));

function Settings() {
  const classes = useStyles();
  const ref = useRef(null);
  const { settings, saveSettings } = useSettings();
  const [isOpen, setOpen] = useState(false);
  const [redir, setRedir] = useState(false);
  const [clientOptions, setClientOptions] = useState();
  const { keycloak } = useKeycloak();

  const [values, setValues] = useState({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme,
    project: settings.project
  });

  useEffect(() => {
    const data = keycloak.tokenParsed.resource_access;
    const opts = [];
    for (const client in data) {
      if (client !== 'account') opts.push(client.toUpperCase());
    }
    setClientOptions(opts);
  }, [keycloak.tokenParsed.resource_access]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (field, value) => {
    setValues({
      ...values,
      [field]: value
    });

    if (field === 'project') setRedir(true);
  };

  const handleSave = () => {
    saveSettings(values);
    setOpen(false);
    if (redir) {
      setRedir(false);
      const cur = window.location.href;
      window.location.href = cur;
    }
  };

  return (
    <>
      <SvgIcon fontSize="small">
        <UserIcon />
      </SvgIcon>
      <div style={{ verticalAlign: 'bottom' }}>
        <Typography>{keycloak.tokenParsed.name}</Typography>
      </div>
      <Tooltip title="Settings">
        <Badge
          color="secondary"
          variant="dot"
          classes={{ badge: classes.badge }}
        >
          <IconButton color="inherit" onClick={handleOpen} ref={ref}>
            <SvgIcon fontSize="small">
              <SettingsIcon />
            </SvgIcon>
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Typography variant="h4" color="textPrimary">
          Settings
        </Typography>
        <Box>
          <Logout />
        </Box>
        <Box mt={2}>
          <TextField
            fullWidth
            label="Project"
            name="project"
            onChange={(event) => handleChange('project', event.target.value)}
            select
            SelectProps={{ native: true }}
            value={values.project}
            variant="outlined"
          >
            {clientOptions?.map((a) => <option value={a} key={a}>{a}</option>)}
          </TextField>
        </Box>

        <Box mt={2} px={1}>
          <FormControlLabel
            control={(
              <Switch
                checked={values.direction === 'rtl'}
                edge="start"
                name="direction"
                onChange={(event) => handleChange(
                  'direction',
                  event.target.checked ? 'rtl' : 'ltr'
                )}
              />
            )}
            label="RTL"
          />
        </Box>
        <Box mt={2} px={1}>
          <FormControlLabel
            control={(
              <Switch
                checked={values.responsiveFontSizes}
                edge="start"
                name="direction"
                onChange={(event) => handleChange('responsiveFontSizes', event.target.checked)}
              />
            )}
            label="Responsive font sizes"
          />
        </Box>
        <Box mt={2}>
          <TextField
            fullWidth
            label="Theme"
            name="theme"
            onChange={(event) => handleChange('theme', event.target.value)}
            select
            SelectProps={{ native: true }}
            value={values.theme}
            variant="outlined"
          >
            {Object.keys(THEMES).map((theme) => (
              <option key={theme} value={theme}>
                {capitalCase(theme)}
              </option>
            ))}
          </TextField>
        </Box>
        <Box mt={2}>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={handleSave}
          >
            Save Settings
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default Settings;
